/**
 * Actions par jour — aplat, une seule série.
 *
 * Une série unique sur un axe de temps : aplat plutôt que barres, et pas de
 * légende — le titre de la carte dit déjà ce qui est tracé.
 *
 * ⚠️ Cette série et « Utilisateurs actifs » ne partagent PAS un graphique. Des
 * dizaines de milliers d'actions et quelques centaines d'utilisateurs sur deux
 * échelles superposées produiraient un axe double, qui laisse croire à des
 * croisements et des écarts qui n'existent pas. Deux cartes, un axe chacune.
 */

import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { useChartMarkers } from '../hooks/useChartMarkers';
import { formatCompact, formatDayLong, formatDayShort, formatInteger } from '../lib/format';
import { AREA_FILL_OPACITY, AXIS_STYLE, COLORS, LINE_WIDTH } from '../lib/theme';
import type { DailyActivityPoint, DataColumn } from '../types';
import { ChartCard } from './Card';
import { ChartTooltip } from './ChartTooltip';
import { MarkerStrip } from './MarkerStrip';

const columns: ReadonlyArray<DataColumn<DailyActivityPoint>> = [
  { key: 'date', header: 'Jour', render: (row) => formatDayLong(row.date) },
  { key: 'actions', header: 'Actions', numeric: true, render: (row) => formatInteger(row.actions) },
];

/** Série de flux : les actions d'un jour ne contiennent pas celles de la veille. */
const MARKER_CONFIG = {
  value: (row: DailyActivityPoint) => row.actions,
  mode: 'level',
  unit: 'count',
  higherIsBetter: true,
  scope: 'analytics',
} as const;

export function ActionsPerDayChart({ data }: { data: readonly DailyActivityPoint[] }) {
  const markers = useChartMarkers(data, MARKER_CONFIG);

  return (
    <ChartCard
      title="Actions par jour"
      subtitle="Toutes actions confondues, en heure de Paris"
      rows={data}
      columns={columns}
      footer={<MarkerStrip items={markers.items} />}
    >
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data as DailyActivityPoint[]} margin={{ top: 4, right: 12, bottom: 0, left: 0 }}>
          {/* Grille horizontale seulement : les verticales n'aident pas a lire
              une valeur et ajoutent de l'encre qui n'est pas de la donnee. */}
          <CartesianGrid stroke={COLORS.grid} vertical={false} />
          <XAxis
            dataKey="date"
            tickFormatter={formatDayShort}
            tick={{ fill: AXIS_STYLE.fill, fontSize: AXIS_STYLE.fontSize }}
            axisLine={{ stroke: COLORS.grid }}
            tickLine={false}
            minTickGap={40}
          />
          <YAxis
            tickFormatter={formatCompact}
            tick={{ fill: AXIS_STYLE.fill, fontSize: AXIS_STYLE.fontSize }}
            axisLine={false}
            tickLine={false}
            width={52}
          />
          <Tooltip
            // Le reticule trouve le X : le lecteur vise une date, jamais un
            // trait de 2 px.
            cursor={{ stroke: COLORS.textMuted, strokeWidth: 1 }}
            content={
              <ChartTooltip labelFormatter={formatDayLong} valueFormatter={formatInteger} />
            }
          />
          {markers.lines}
          <Area
            isAnimationActive={false}
            type="monotone"
            dataKey="actions"
            name="Actions"
            stroke={COLORS.series1}
            strokeWidth={LINE_WIDTH}
            fill={COLORS.series1}
            fillOpacity={AREA_FILL_OPACITY}
            // Pas de point sur chaque jour : sur 365 points ils forment une
            // bouillie. Seul le point survole apparait, avec son anneau de
            // surface pour rester lisible par-dessus le trait.
            dot={false}
            activeDot={{ r: 4, fill: COLORS.series1, stroke: COLORS.surface, strokeWidth: 2 }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}
